import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    CircularProgress,
} from '@mui/material';
import api from '../../services/api';
import { Category } from '../../types/Category';

interface RenameDocumentDialogProps {
    open: boolean;
    onClose: () => void;
    onRename: (newName: string) => void;
    originalName: string;
    existingNames: string[];
    documentName: string;
    categoryId?: string;
}

export const RenameDocumentDialog: React.FC<RenameDocumentDialogProps> = ({
    open,
    onClose,
    onRename,
    originalName,
    existingNames,
    documentName,
    categoryId,
}) => {
    const [newName, setNewName] = useState(originalName);
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [subCategories, setSubCategories] = useState<string[]>([]);
    const [loadingSubCategories, setLoadingSubCategories] = useState(false);

    useEffect(() => {
        if (open) {
            setNewName(originalName);
            setError(null);
            setIsSubmitting(false);
        }
    }, [open, originalName]);

    useEffect(() => {
        if (!open || !documentName || !categoryId) {
            setSubCategories([]);
            return;
        }

        let cancelled = false;
        setLoadingSubCategories(true);

        (async () => {
            try {
                await api.post('/documents/open', null, {
                    params: { documentId: documentName },
                });
                if (cancelled) return;

                const res = await api.get('/categories/subcategories', {
                    params: { categoryId },
                });
                if (!cancelled) {
                    setSubCategories(res.data.map((c: Category) => c.name));
                }
            } catch (err) {
                console.error('Failed to load subcategories for rename', err);
                if (!cancelled) {
                    setSubCategories([]);
                }
            } finally {
                if (!cancelled) {
                    setLoadingSubCategories(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, documentName, categoryId]);

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setNewName(value);

        if (!value.trim()) {
            setError('Document name cannot be empty');
        } else if (existingNames.includes(value)) {
            setError('A document with this name already exists');
        } else if (subCategories.includes(value)) {
            setError('Document contains a sub category with this name');
        } else {
            setError(null);
        }
    };

    const handleConfirm = async () => {
        if (!error && newName.trim() && !loadingSubCategories) {
            setIsSubmitting(true);
            onRename(newName.trim());
            onClose();
        }
    };

    return (
        <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
            <DialogTitle>Rename Document</DialogTitle>
            <DialogContent>
                {loadingSubCategories ? (
                    <Box sx={{ my: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={18} />
                        <Typography variant="body2">Loading document...</Typography>
                    </Box>
                ) : (
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Document Name"
                        type="text"
                        fullWidth
                        value={newName}
                        onChange={handleNameChange}
                        error={!!error}
                        helperText={error}
                        disabled={isSubmitting}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                <Button
                    onClick={handleConfirm}
                    color="primary"
                    disabled={!!error || !newName.trim() || isSubmitting || loadingSubCategories}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                >
                    {isSubmitting ? 'Loading...' : 'Confirm'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
